import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { toast } from 'sonner';
import { approvalsApi } from '@/services/approvalsApi';
import { auditService } from '@/services/auditService';
import { rejectionService } from '@/services/rejectionService';
import { getApprovalErrorMessage } from '@/utils/approvalErrorMessage';
import { getDelegationService } from '@/features/delegation/services/delegationService';
import { REJECTION_MIN_CHARS } from '@/components/approvals/RejectTimesheetDialog';
import { BULK_REJECT_MIN_CHARS } from '@/components/approvals/BulkRejectDialog';
import { BULK_CONFIRMATION_THRESHOLD } from '@/utils/ApprovalDashboard/constants';
import type { ApprovalRow } from '@/components/ApprovalGrid';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import type { PolicyViolation } from '@/data/mockPolicyViolations';
import type { Employee, Project, Timesheet } from '@/types/timesheet';

interface PostApprovalConfirmState {
  timesheet: Timesheet;
  message: string;
  notes?: string;
}

interface UseApprovalActionsParams {
  screenMode: 'demo' | 'live';
  currentEmployee: Employee;
  projects: Project[];
  updateTimesheet: (timesheet: Timesheet) => void;
  timesheetAnomalies: Map<number, AnomalyDetectionResult>;
  timesheetPolicyViolations: Map<number, PolicyViolation[]>;
  selectedRows: ApprovalRow[];
  setClearSelection: Dispatch<SetStateAction<boolean>>;
  refreshPendingApprovals: () => Promise<void>;
  canViewDetectedIssues: () => boolean;
  canViewPolicyViolations: () => boolean;
  requiresRejectionReason: () => boolean;
  rejectingTimesheet: Timesheet | null;
  setRejectingTimesheet: Dispatch<SetStateAction<Timesheet | null>>;
  setViewingTimesheet: Dispatch<SetStateAction<Timesheet | null>>;
}

export interface UseApprovalActionsReturn {
  approvingIds: Set<number>;
  rejectingIds: Set<number>;
  isBulkApproving: boolean;
  isBulkRejecting: boolean;
  isBulkProcessing: boolean;
  isRejectingSingle: boolean;
  isConfirmingApproval: boolean;
  approvalNotes: string;
  setApprovalNotes: Dispatch<SetStateAction<string>>;
  bulkApprovalNotes: string;
  setBulkApprovalNotes: Dispatch<SetStateAction<string>>;
  showBulkConfirmDialog: boolean;
  setShowBulkConfirmDialog: Dispatch<SetStateAction<boolean>>;
  showBulkRejectDialog: boolean;
  setShowBulkRejectDialog: Dispatch<SetStateAction<boolean>>;
  pendingFlaggedApproval: Timesheet | null;
  setPendingFlaggedApproval: Dispatch<SetStateAction<Timesheet | null>>;
  postApprovalConfirm: PostApprovalConfirmState | null;
  approveTimesheet: (timesheet: Timesheet, notes?: string, confirmed?: boolean) => Promise<void>;
  handleApproveClick: (timesheet: Timesheet, opts?: { fromViewDialog?: boolean }) => void;
  handleGridApprove: (timesheet: Timesheet) => void;
  handleToolbarBulkApprove: () => void;
  handleBulkRejectClick: () => void;
  executeBulkApprove: () => Promise<void>;
  executeBulkReject: (reason: string) => Promise<void>;
  rejectTimesheet: (reason: string) => Promise<void>;
  handlePostApprovalAccept: () => Promise<void>;
  handlePostApprovalDecline: () => void;
  cancelBulkApproval: () => void;
}

/**
 * Owns every approve/reject action for the approval queue, including the
 * in-flight bookkeeping that keeps rows and bulk actions disabled while a
 * single or batch operation is running.
 */
export function useApprovalActions({
  screenMode,
  currentEmployee,
  projects,
  updateTimesheet,
  timesheetAnomalies,
  timesheetPolicyViolations,
  selectedRows,
  setClearSelection,
  refreshPendingApprovals,
  canViewDetectedIssues,
  canViewPolicyViolations,
  requiresRejectionReason,
  rejectingTimesheet,
  setRejectingTimesheet,
  setViewingTimesheet,
}: UseApprovalActionsParams): UseApprovalActionsReturn {
  const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);
  const [, setPendingBulkAction] = useState<'approve' | null>(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [bulkApprovalNotes, setBulkApprovalNotes] = useState('');
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [pendingFlaggedApproval, setPendingFlaggedApproval] = useState<Timesheet | null>(null);
  // Post-approval rule confirmation dialog (Accept / Decline) shown to the approver
  // when approving would trigger a post-stage rule that carries a user-facing message.
  const [postApprovalConfirm, setPostApprovalConfirm] = useState<PostApprovalConfirmState | null>(null);
  const [isConfirmingApproval, setIsConfirmingApproval] = useState(false);
  // Timesheets with an approve/reject request currently in flight. Every timesheet
  // included in a running (single or bulk) operation is held here so its row and the
  // bulk actions stay disabled until the whole operation finishes.
  const [approvingIds, setApprovingIds] = useState<Set<number>>(new Set());
  const [rejectingIds, setRejectingIds] = useState<Set<number>>(new Set());
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isBulkRejecting, setIsBulkRejecting] = useState(false);
  const [isRejectingSingle, setIsRejectingSingle] = useState(false);
  const isBulkProcessing = isBulkApproving || isBulkRejecting;

  const markInFlight = (
    setIds: Dispatch<SetStateAction<Set<number>>>,
    ids: number[],
    inFlight: boolean
  ) => {
    setIds(prev => {
      const next = new Set(prev);
      for (const id of ids) {
        if (inFlight) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const runApproveTimesheet = async (timesheet: Timesheet, notes?: string, confirmed = false) => {
    if (screenMode === 'live') {
      try {
        const notesToSend = notes?.trim() || undefined;
        const response = await approvalsApi.approveTimesheet(
          timesheet.id, notesToSend, confirmed, timesheet.queueOwnerId ?? undefined);

        // If a post-approval rule requires confirmation, show the Accept/Decline dialog
        // and halt this approval until the approver confirms.
        if (response.requiresConfirmation && response.confirmationMessage) {
          setPostApprovalConfirm({ timesheet, message: response.confirmationMessage, notes: notesToSend });
          return;
        }

        // Show appropriate message based on workflow status
        if (response.workflowCompleted && response.finalStatus === 'approved') {
          toast.success(`Timesheet for ${timesheet.employeeName} fully approved`);
        } else if (response.advancedToNextStage) {
          toast.success(`Approved stage for ${timesheet.employeeName}. ${response.newStageName ? `Now at: ${response.newStageName}` : ''}`);
        } else {
          toast.success(`Approved timesheet for ${timesheet.employeeName}`);
        }

        // Refresh pending approvals list
        refreshPendingApprovals();
      } catch (error) {
        console.error('Failed to approve timesheet:', error);
        toast.error(`Failed to approve timesheet for ${timesheet.employeeName}`, {
          description: getApprovalErrorMessage(error),
        });
      }
    } else {
      // Demo mode logic
      const updated = {
        ...timesheet,
        status: 'approved' as const,
        approvedAt: new Date(),
        approverId: currentEmployee.id,
        approverName: currentEmployee.name
      };
      updateTimesheet(updated);

      // Log audit entry for demo mode, including delegation context when applicable
      getDelegationService('demo').getDelegatedToMe().then((delegatedToMe) => {
        const activeDelegation = delegatedToMe.find(d => d.status === 'active');
        auditService.logAction(
          timesheet.id,
          'approved',
          currentEmployee.id,
          currentEmployee.name,
          {
            actorRole: 'approver',
            previousStatus: 'submitted',
            newStatus: 'approved',
            isDelegated: !!activeDelegation,
            delegatedFromManagerName: activeDelegation?.managerName,
            delegatedFromManagerId: activeDelegation?.managerId,
          }
        );
      }).catch(() => {
        // Audit log failure should not affect UX — log silently
        auditService.logAction(
          timesheet.id,
          'approved',
          currentEmployee.id,
          currentEmployee.name,
          { actorRole: 'approver', previousStatus: 'submitted', newStatus: 'approved' }
        );
      });

      toast.success(`Approved timesheet for ${timesheet.employeeName}`);
    }
  };

  const approveTimesheet = async (timesheet: Timesheet, notes?: string, confirmed = false) => {
    // Guard: block self-approval when cannotApproveOwnTimesheet is set
    if (timesheet.cannotApproveOwnTimesheet === true && timesheet.employeeId === currentEmployee.id) {
      toast.error('Cannot approve own timesheet');
      return;
    }

    markInFlight(setApprovingIds, [timesheet.id], true);
    try {
      await runApproveTimesheet(timesheet, notes, confirmed);
    } finally {
      markInFlight(setApprovingIds, [timesheet.id], false);
    }
  };

  // Handlers for the post-approval rule confirmation dialog (Accept / Decline)
  const handlePostApprovalAccept = async () => {
    if (!postApprovalConfirm) return;
    const { timesheet, notes } = postApprovalConfirm;
    setIsConfirmingApproval(true);
    try {
      await approveTimesheet(timesheet, notes, true);
    } finally {
      setIsConfirmingApproval(false);
      setPostApprovalConfirm(null);
    }
  };

  const handlePostApprovalDecline = () => {
    toast.info('Approval cancelled');
    setPostApprovalConfirm(null);
  };

  // Intercept single-item approve: show confirmation dialog if the timesheet has flags,
  // otherwise call approveTimesheet() directly to preserve the fast one-click path.
  const handleApproveClick = (timesheet: Timesheet, opts?: { fromViewDialog?: boolean }) => {
    const anomalyResult = timesheetAnomalies.get(timesheet.id);
    const policyViolations = timesheetPolicyViolations.get(timesheet.id) || [];
    // Only treat as flagged for issue types the current user has permission to see.
    // This prevents the confirmation dialog appearing for issues that are invisible to them.
    const isFlagged =
      (canViewDetectedIssues() && anomalyResult?.hasAnomalies === true) ||
      (canViewPolicyViolations() && policyViolations.length > 0);

    if (isFlagged) {
      // Close the view dialog first so the AlertDialog sits on top cleanly
      if (opts?.fromViewDialog) {
        setViewingTimesheet(null);
      }
      setPendingFlaggedApproval(timesheet);
    } else {
      if (opts?.fromViewDialog) {
        setViewingTimesheet(null);
      }
      approveTimesheet(timesheet);
    }
  };

  // Stable identity for the grid's approve handler so the memoized ApprovalGrid
  // (and its column definitions) are not rebuilt on every dashboard render.
  const handleApproveClickRef = useRef(handleApproveClick);
  handleApproveClickRef.current = handleApproveClick;
  const handleGridApprove = useCallback((timesheet: Timesheet) => {
    handleApproveClickRef.current(timesheet);
  }, []);

  const executeBulkApprove = async () => {
    if (isBulkApproving) return;

    const allSelected = selectedRows.map(r => r.timesheet);
    // Filter out own timesheets where self-approval is blocked
    const toApprove = allSelected.filter(
      ts => !(ts.cannotApproveOwnTimesheet === true && ts.employeeId === currentEmployee.id)
    );
    const skippedCount = allSelected.length - toApprove.length;
    const notes = bulkApprovalNotes;

    // Show progress for large batches
    if (toApprove.length > 20) {
      toast.loading(`Approving ${toApprove.length} timesheets...`, {
        id: 'bulk-approve',
      });
    }

    // Lock every timesheet in the batch for the whole run, not just the one currently
    // being sent, so no row can be actioned again while the batch is still going.
    const batchIds = toApprove.map(ts => ts.id);
    setIsBulkApproving(true);
    markInFlight(setApprovingIds, batchIds, true);

    try {
      for (const ts of toApprove) {
        // Bulk approval is an explicit batch action — auto-confirm post-approval rule
        // messages so an individual timesheet does not silently stall the batch.
        await runApproveTimesheet(ts, notes, true);
      }

      // Clear TanStack selection
      setClearSelection(true);

      // Dismiss loading toast and show success
      toast.dismiss('bulk-approve');
      if (toApprove.length > 0) {
        toast.success(`Successfully approved ${toApprove.length} timesheets`);
      }
      if (skippedCount > 0) {
        toast.warning(`${skippedCount} own timesheet${skippedCount !== 1 ? 's' : ''} skipped (cannot approve own)`);
      }
    } finally {
      markInFlight(setApprovingIds, batchIds, false);
      setIsBulkApproving(false);
      setShowBulkConfirmDialog(false);
      setPendingBulkAction(null);
      setBulkApprovalNotes('');
    }
  };

  const handleBulkApproveClick = () => {
    const toApprove = selectedRows.map(r => r.timesheet);

    // Show confirmation for large selections or items with anomalies
    const itemsWithAnomalies = toApprove.filter(ts => {
      const anomalyResult = timesheetAnomalies.get(ts.id);
      return anomalyResult?.hasAnomalies;
    });

    if (toApprove.length >= BULK_CONFIRMATION_THRESHOLD || itemsWithAnomalies.length > 0) {
      setPendingBulkAction('approve');
      setShowBulkConfirmDialog(true);
    } else {
      executeBulkApprove();
    }
  };

  // Stable identity for the toolbar slot memo (see handleGridApprove).
  const handleBulkApproveClickRef = useRef(handleBulkApproveClick);
  handleBulkApproveClickRef.current = handleBulkApproveClick;
  const handleToolbarBulkApprove = useCallback(() => {
    handleBulkApproveClickRef.current();
  }, []);

  // Cancel handler for the bulk approval confirmation dialog
  const cancelBulkApproval = () => {
    setShowBulkConfirmDialog(false);
    setPendingBulkAction(null);
    setBulkApprovalNotes('');
  };

  // F-12: Bulk Reject
  const handleBulkRejectClick = useCallback(() => {
    if (selectedRows.length > 0) {
      setShowBulkRejectDialog(true);
    }
  }, [selectedRows]);

  // Demo mode: writes the RejectionRecord, then marks the timesheet rejected locally.
  const recordDemoRejection = async (ts: Timesheet, reason: string) => {
    rejectionService.setMode(false);

    await rejectionService.recordRejection(
      ts,
      reason,
      currentEmployee.id,
      currentEmployee.name,
      projects,
      [], // costCentres - not needed for basic rejection tracking
      {
        approverRole: currentEmployee.role || 'manager'
      }
    );

    updateTimesheet({
      ...ts,
      status: 'rejected' as const,
      rejectedAt: new Date(),
      rejectionReason: reason,
      approverId: currentEmployee.id,
      approverName: currentEmployee.name
    });
  };

  const executeBulkReject = async (reason: string) => {
    if (isBulkRejecting) return;
    if (requiresRejectionReason() && reason.length < BULK_REJECT_MIN_CHARS) return;

    const allSelected = selectedRows.map(r => r.timesheet);
    // Filter out own timesheets where self-rejection is blocked
    const toReject = allSelected.filter(
      ts => !(ts.cannotApproveOwnTimesheet === true && ts.employeeId === currentEmployee.id)
    );
    const skippedCount = allSelected.length - toReject.length;

    if (toReject.length > 5) {
      toast.loading(`Rejecting ${toReject.length} timesheets...`, { id: 'bulk-reject' });
    }

    // Lock every timesheet in the batch for the whole run (see executeBulkApprove).
    const batchIds = toReject.map(ts => ts.id);
    setIsBulkRejecting(true);
    markInFlight(setRejectingIds, batchIds, true);

    try {
      for (const ts of toReject) {
        if (screenMode === 'live') {
          await approvalsApi.rejectTimesheet(ts.id, reason);
        } else {
          await recordDemoRejection(ts, reason);
        }
      }

      if (screenMode === 'live') {
        refreshPendingApprovals();
      }

      toast.dismiss('bulk-reject');
      if (toReject.length > 0) {
        toast.success(`${toReject.length} timesheet${toReject.length !== 1 ? 's' : ''} rejected.`);
      }
      if (skippedCount > 0) {
        toast.warning(`${skippedCount} own timesheet${skippedCount !== 1 ? 's' : ''} skipped (cannot reject own)`);
      }
    } catch (error) {
      console.error('Failed to bulk reject timesheets:', error);
      toast.dismiss('bulk-reject');
      toast.error('Failed to reject one or more timesheets', {
        description: getApprovalErrorMessage(error),
      });
    } finally {
      markInFlight(setRejectingIds, batchIds, false);
      setIsBulkRejecting(false);
      setClearSelection(true);
      setShowBulkRejectDialog(false);
    }
  };

  const rejectTimesheet = async (reason: string) => {
    if (isRejectingSingle) return;

    // Guard: block self-rejection when cannotApproveOwnTimesheet is set
    if (rejectingTimesheet?.cannotApproveOwnTimesheet === true && rejectingTimesheet?.employeeId === currentEmployee.id) {
      toast.error('Cannot reject own timesheet');
      setRejectingTimesheet(null);
      return;
    }

    const meetsMinimum = reason.length >= REJECTION_MIN_CHARS;
    if (!rejectingTimesheet || (requiresRejectionReason() && !meetsMinimum)) {
      toast.error(!meetsMinimum
        ? `Please provide a rejection reason (minimum ${REJECTION_MIN_CHARS} characters)`
        : 'Unable to reject timesheet');
      return;
    }

    const reasonToSubmit = reason;
    const rejectedId = rejectingTimesheet.id;

    setIsRejectingSingle(true);
    markInFlight(setRejectingIds, [rejectedId], true);

    try {
      if (screenMode === 'live') {
        // Use dedicated API endpoint in live mode
        await approvalsApi.rejectTimesheet(rejectingTimesheet.id, reasonToSubmit);
        toast.success(`Rejected timesheet for ${rejectingTimesheet.employeeName}`);
        // Refresh pending approvals list
        refreshPendingApprovals();
      } else {
        await recordDemoRejection(rejectingTimesheet, reasonToSubmit);
        toast.success(`Rejected timesheet for ${rejectingTimesheet.employeeName}`);
      }
    } catch (error) {
      console.error('Failed to reject timesheet:', error);
      toast.error('Failed to reject timesheet', {
        description: getApprovalErrorMessage(error),
      });
    } finally {
      markInFlight(setRejectingIds, [rejectedId], false);
      setIsRejectingSingle(false);
      // Always close the modal so the user is not stuck
      setRejectingTimesheet(null);
    }
  };

  return {
    approvingIds,
    rejectingIds,
    isBulkApproving,
    isBulkRejecting,
    isBulkProcessing,
    isRejectingSingle,
    isConfirmingApproval,
    approvalNotes,
    setApprovalNotes,
    bulkApprovalNotes,
    setBulkApprovalNotes,
    showBulkConfirmDialog,
    setShowBulkConfirmDialog,
    showBulkRejectDialog,
    setShowBulkRejectDialog,
    pendingFlaggedApproval,
    setPendingFlaggedApproval,
    postApprovalConfirm,
    approveTimesheet,
    handleApproveClick,
    handleGridApprove,
    handleToolbarBulkApprove,
    handleBulkRejectClick,
    executeBulkApprove,
    executeBulkReject,
    rejectTimesheet,
    handlePostApprovalAccept,
    handlePostApprovalDecline,
    cancelBulkApproval,
  };
}
